import { useState, useEffect, useCallback } from 'react';
import { apiClient, useAuth } from '../contexts/AuthContext';
import { BottomNav } from '../components/BottomNav';
import { MediaUpload } from '../components/MediaUpload';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Flame, Plus, Check, Trophy, Clock, Share2, Trash2, Star, Users, CalendarCheck, Image, Send, ShieldCheck, X, Pencil
} from 'lucide-react';
import { toast } from 'sonner';

export default function ChallengesPage({ embedded = false }) {
  const { user } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', event_date: '', duration_days: 7, reward_points: 10, reward_badge: '' });

  const [mediaUrl, setMediaUrl] = useState({});
  const [mediaCaption, setMediaCaption] = useState({});
  const [joinGroupSize, setJoinGroupSize] = useState({});
  const [showJoinDialog, setShowJoinDialog] = useState(null);
  const [showProofDialog, setShowProofDialog] = useState(null);
  const [proofPhoto, setProofPhoto] = useState('');
  const [proofNote, setProofNote] = useState('');

  const fetchChallenges = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/challenges');
      setChallenges(data);
    } catch {
      toast.error('Erreur chargement challenges');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChallenges(); }, [fetchChallenges]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/challenges', {
        ...form,
        event_date: form.event_date || null,
        reward_badge: form.reward_badge || null
      });
      toast.success('Challenge créé !');
      setShowCreate(false);
      setForm({ title: '', description: '', event_date: '', duration_days: 7, reward_points: 10, reward_badge: '' });
      fetchChallenges();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleEdit = (ch) => {
    setEditingChallenge(ch);
    setForm({
      title: ch.title || '',
      description: ch.description || '',
      event_date: ch.event_date || '',
      duration_days: ch.duration_days || 7,
      reward_points: ch.reward_points || 10,
      reward_badge: ch.reward_badge || ''
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingChallenge) return;
    try {
      await apiClient.put(`/challenges/${editingChallenge.id}`, {
        title: form.title,
        description: form.description,
        event_date: form.event_date || null,
        duration_days: form.duration_days,
        reward_points: form.reward_points
      });
      toast.success('Challenge modifié !');
      setEditingChallenge(null);
      setForm({ title: '', description: '', event_date: '', duration_days: 7, reward_points: 10, reward_badge: '' });
      fetchChallenges();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleJoin = async (id) => {
    const groupSize = joinGroupSize[id] || 1;
    try {
      await apiClient.post(`/challenges/${id}/join`, { group_size: groupSize });
      toast.success(`Challenge relevé ! (${groupSize} personne${groupSize > 1 ? 's' : ''})`);
      setShowJoinDialog(null);
      setJoinGroupSize({ ...joinGroupSize, [id]: 1 });
      fetchChallenges();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleComplete = async (id) => {
    try {
      const payload = {};
      if (proofPhoto) payload.proof_photo = proofPhoto;
      if (proofNote) payload.proof_note = proofNote;
      const { data } = await apiClient.post(`/challenges/${id}/complete`, payload);
      toast.success(data.message);
      setShowProofDialog(null);
      setProofPhoto('');
      setProofNote('');
      fetchChallenges();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleValidate = async (challengeId, userId) => {
    try {
      const { data } = await apiClient.put(`/challenges/${challengeId}/validate/${userId}`);
      toast.success(data.message);
      fetchChallenges();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleReject = async (challengeId, userId) => {
    try {
      const { data } = await apiClient.put(`/challenges/${challengeId}/reject/${userId}`);
      toast.info(data.message);
      fetchChallenges();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce challenge ?')) return;
    try {
      await apiClient.delete(`/challenges/${id}`);
      toast.success('Challenge supprimé');
      fetchChallenges();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleShare = (challenge) => {
    const text = `Je relève le challenge "${challenge.title}" avec La Meute ! ${challenge.description}`;
    const url = `https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleAddMedia = async (challengeId) => {
    const url = mediaUrl[challengeId];
    if (!url?.trim()) { toast.error('URL requise'); return; }
    const isVideo = url.includes('youtube') || url.includes('youtu.be') || url.includes('.mp4');
    try {
      await apiClient.post(`/challenges/${challengeId}/media`, {
        media_type: isVideo ? 'video' : 'photo',
        url: url.trim(),
        caption: mediaCaption[challengeId] || ''
      });
      toast.success('Média ajouté !');
      setMediaUrl({ ...mediaUrl, [challengeId]: '' });
      setMediaCaption({ ...mediaCaption, [challengeId]: '' });
      fetchChallenges();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const getDaysLeft = (deadline) => {
    const diff = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    if (diff <= 0) return 'Terminé';
    return `${diff}j restants`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className={`${embedded ? "" : "min-h-screen"} bg-[#0F0F0F] ${embedded ? "pb-4" : "pb-20"}`}>
      {!embedded && <header className="sticky top-0 z-40 bg-[#0F0F0F]/95 backdrop-blur-sm border-b border-[#2A2A2E]">
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <h1 className="text-xl font-bold text-white">Challenges</h1>
            <p className="text-xs text-zinc-500">Releve les defis de la meute</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-[#FF6B35] text-black hover:bg-[#E85A25]" size="sm" data-testid="create-challenge-btn">
            <Plus className="w-4 h-4 mr-1" /> Creer
          </Button>
        </div>
      </header>}
      {embedded && (
        <div className="flex justify-end px-4 pt-3">
          <Button onClick={() => setShowCreate(true)} className="bg-[#FF6B35] text-black hover:bg-[#E85A25]" size="sm" data-testid="create-challenge-btn">
            <Plus className="w-4 h-4 mr-1" /> Creer un defi
          </Button>
        </div>
      )}

      <main className="p-4 space-y-4 animate-fade-in">
        {challenges.length > 0 ? challenges.map((ch) => {
          const participants = ch.participants || [];
          const myParticipation = participants.find(p => p.user_id === user?.id);
          const isJoined = !!myParticipation;
          const isCompleted = myParticipation?.completed;
          const isExpired = getDaysLeft(ch.deadline) === 'Terminé';
          const completedCount = participants.filter(p => p.completed).length;
          const pendingCount = participants.filter(p => p.proof_submitted && !p.completed).length;
          const isProofSubmitted = myParticipation?.proof_submitted;
          const totalPeople = participants.reduce((sum, p) => sum + (p.group_size || 1), 0);

          return (
            <div key={ch.id} className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm overflow-hidden" data-testid={`challenge-${ch.id}`}>
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-[#FF6B35]/20 flex items-center justify-center">
                      <Flame className="w-5 h-5 text-[#FF6B35]" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-sm">{ch.title}</h3>
                      <p className="text-xs text-zinc-400">par {ch.creator_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {ch.event_date && (
                      <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-sm flex items-center gap-1">
                        <CalendarCheck className="w-3 h-3" />{new Date(ch.event_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                      </span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-sm ${isExpired ? 'bg-zinc-800 text-zinc-500' : 'bg-[#FF6B35]/20 text-[#FF6B35]'}`}>
                      <Clock className="w-3 h-3 inline mr-0.5" />{getDaysLeft(ch.deadline)}
                    </span>
                    {(ch.creator_id === user?.id || user?.role === 'admin') && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleEdit(ch)} className="text-zinc-500 hover:text-[#FF6B35]" data-testid={`edit-defi-${ch.id}`}>
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(ch.id)} className="text-zinc-500 hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-sm text-zinc-400 mb-3">{ch.description}</p>

                {/* Rewards */}
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-sm flex items-center gap-1">
                    <Star className="w-3 h-3" /> +{ch.reward_points} pts
                  </span>
                  {ch.reward_badge && (
                    <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-sm flex items-center gap-1">
                      <Trophy className="w-3 h-3" /> {ch.reward_badge}
                    </span>
                  )}
                  <span className="text-xs text-zinc-500 flex items-center gap-1 ml-auto">
                    <Users className="w-3 h-3" /> {totalPeople} personne{totalPeople > 1 ? 's' : ''}
                    {participants.length !== totalPeople && <span className="text-zinc-600">({participants.length} inscrit{participants.length > 1 ? 's' : ''})</span>}
                    {completedCount > 0 && <span className="text-green-400">({completedCount} valide{completedCount > 1 ? 's' : ''})</span>}
                    {pendingCount > 0 && <span className="text-[#FFB800]">({pendingCount} en attente)</span>}
                  </span>
                </div>

                {/* Participants */}
                {participants.length > 0 && (
                  <div className="space-y-1.5 mb-3">
                    {participants.map((p) => (
                      <div key={p.user_id}
                        className={`flex items-center gap-2 px-2.5 py-1.5 rounded-sm text-xs ${
                          p.completed ? 'bg-green-500/10 border border-green-500/20' :
                          p.proof_submitted ? 'bg-[#FFB800]/10 border border-[#FFB800]/20' :
                          'bg-zinc-800/50 border border-zinc-800'
                        }`}>
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${
                          p.completed ? 'bg-green-500/30 text-green-400' :
                          p.proof_submitted ? 'bg-[#FFB800]/30 text-[#FFB800]' :
                          'bg-zinc-700 text-zinc-500'
                        }`}>
                          {p.completed ? <Check className="w-3 h-3" /> : p.proof_submitted ? <Clock className="w-3 h-3" /> : '·'}
                        </span>
                        <span className={`flex-1 ${p.completed ? 'text-green-400' : p.proof_submitted ? 'text-[#FFB800]' : 'text-zinc-400'}`}>
                          {p.name}
                          {(p.group_size || 1) > 1 && <span className="text-zinc-500 ml-1">(+{(p.group_size || 1) - 1})</span>}
                        </span>
                        {p.proof_photo && (
                          <a href={p.proof_photo} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-sm overflow-hidden border border-[#2A2A2E] flex-shrink-0">
                            <img src={p.proof_photo} alt="preuve" className="w-full h-full object-cover" />
                          </a>
                        )}
                        {p.completed && <span className="text-[9px] text-green-500 font-bold">Valide</span>}
                        {p.proof_submitted && !p.completed && <span className="text-[9px] text-[#FFB800] font-bold">Preuve soumise</span>}
                        {/* Admin validate/reject buttons */}
                        {user?.role === 'admin' && p.proof_submitted && !p.completed && (
                          <div className="flex gap-1 ml-1">
                            <button onClick={() => handleValidate(ch.id, p.user_id)}
                              className="px-2 py-0.5 bg-green-600 text-white rounded text-[10px] font-bold hover:bg-green-700 flex items-center gap-0.5"
                              data-testid={`validate-${p.user_id}`}>
                              <ShieldCheck className="w-3 h-3" /> Valider
                            </button>
                            <button onClick={() => handleReject(ch.id, p.user_id)}
                              className="px-2 py-0.5 bg-red-600/80 text-white rounded text-[10px] font-bold hover:bg-red-700 flex items-center gap-0.5"
                              data-testid={`reject-${p.user_id}`}>
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Media Gallery */}
                {(ch.media || []).length > 0 && (
                  <div className="space-y-2 mb-3">
                    <p className="text-xs text-zinc-400 flex items-center gap-1"><Image className="w-3 h-3" /> Photos & Vidéos</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {ch.media.map((m, mIdx) => (
                        <div key={`${m.url}-${mIdx}`} className="bg-[#0F0F0F] border border-[#2A2A2E] rounded-sm overflow-hidden">
                          {m.media_type === 'photo' ? (
                            <img src={m.url} alt={m.caption || ''} className="w-full h-20 object-cover" />
                          ) : (
                            <div className="w-full h-20 bg-zinc-800 flex items-center justify-center text-zinc-500 text-xs">Video</div>
                          )}
                          <div className="p-1.5">
                            <p className="text-[10px] text-zinc-400 truncate">{m.user_name}</p>
                            {m.caption && <p className="text-[10px] text-zinc-400 truncate">{m.caption}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add Media (if joined) */}
                {isJoined && (
                  <div className="bg-[#0F0F0F] border border-[#2A2A2E] rounded-sm p-2 mb-3">
                    <p className="text-xs text-zinc-400 mb-1.5">Ajouter une photo ou un lien</p>
                    <MediaUpload
                      compact
                      onMediaAdded={async (url) => {
                        const isVideo = url.includes('youtube') || url.includes('youtu.be') || url.includes('.mp4');
                        try {
                          await apiClient.post(`/challenges/${ch.id}/media`, {
                            media_type: isVideo ? 'video' : 'photo',
                            url: url,
                            caption: mediaCaption[ch.id] || ''
                          });
                          toast.success('Média ajouté !');
                          setMediaCaption({ ...mediaCaption, [ch.id]: '' });
                          fetchChallenges();
                        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
                      }}
                    />
                    <div className="flex gap-2 mt-1.5">
                      <input
                        value={mediaUrl[ch.id] || ''}
                        onChange={e => setMediaUrl({...mediaUrl, [ch.id]: e.target.value})}
                        placeholder="Ou coller un lien..."
                        className="flex-1 bg-transparent border border-[#2A2A2E] rounded-sm px-2 py-1 text-xs text-white placeholder-zinc-600"
                      />
                      <button onClick={() => handleAddMedia(ch.id)} className="p-1.5 bg-[#FF6B35]/20 rounded-sm text-[#FF6B35] hover:bg-[#FF6B35]/30">
                        <Send className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <input
                      value={mediaCaption[ch.id] || ''}
                      onChange={e => setMediaCaption({...mediaCaption, [ch.id]: e.target.value})}
                      placeholder="Légende (optionnel)..."
                      className="w-full mt-1.5 bg-transparent border border-[#2A2A2E] rounded-sm px-2 py-1 text-[10px] text-white placeholder-zinc-600"
                    />
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  {!isJoined && !isExpired && (
                    <Button onClick={() => setShowJoinDialog(ch.id)} className="flex-1 bg-[#FF6B35] text-black hover:bg-[#E85A25]" size="sm" data-testid={`join-${ch.id}`}>
                      <Flame className="w-4 h-4 mr-1" /> Je releve ce defi
                    </Button>
                  )}
                  {isJoined && !isCompleted && !isProofSubmitted && !isExpired && (
                    <Button onClick={() => { setShowProofDialog(ch.id); setProofPhoto(''); setProofNote(''); }} className="flex-1 bg-[#FFB800] text-black hover:bg-[#FFD000]" size="sm" data-testid={`complete-${ch.id}`}>
                      <Check className="w-4 h-4 mr-1" /> Soumettre ma preuve
                    </Button>
                  )}
                  {isJoined && isProofSubmitted && !isCompleted && (
                    <div className="flex-1 text-center py-2 bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-sm text-[#FFB800] text-sm font-medium">
                      <Clock className="w-4 h-4 inline mr-1" /> En attente de validation
                    </div>
                  )}
                  {isCompleted && (
                    <div className="flex-1 text-center py-2 bg-green-500/10 border border-green-500/30 rounded-sm text-green-400 text-sm font-medium">
                      <Trophy className="w-4 h-4 inline mr-1" /> Valide par le coach !
                    </div>
                  )}
                  <Button onClick={() => handleShare(ch)} variant="outline" className="border-zinc-700 text-zinc-400 hover:text-white" size="sm" data-testid={`share-${ch.id}`}>
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          );
        }) : (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-8 text-center" data-testid="no-challenges">
            <Flame className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400 mb-2">Aucun challenge en cours</p>
            <p className="text-sm text-zinc-500">Crée le premier défi pour la meute !</p>
          </div>
        )}
      </main>

      {/* Join Dialog — inscription avec nombre de personnes */}
      <Dialog open={!!showJoinDialog} onOpenChange={(open) => { if (!open) setShowJoinDialog(null); }}>
        <DialogContent className="bg-[#1B1B1B] border-[#2A2A2E] text-white max-w-sm">
          <DialogHeader><DialogTitle className="text-white">Relever le defi</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">Combien de personnes participent avec toi ?</p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setJoinGroupSize({ ...joinGroupSize, [showJoinDialog]: Math.max(1, (joinGroupSize[showJoinDialog] || 1) - 1) })}
                className="w-10 h-10 bg-zinc-800 border border-zinc-700 rounded-sm text-white text-xl flex items-center justify-center hover:bg-zinc-700"
              >-</button>
              <span className="text-3xl font-bold text-white font-mono w-12 text-center" data-testid="join-group-size">
                {joinGroupSize[showJoinDialog] || 1}
              </span>
              <button
                type="button"
                onClick={() => setJoinGroupSize({ ...joinGroupSize, [showJoinDialog]: (joinGroupSize[showJoinDialog] || 1) + 1 })}
                className="w-10 h-10 bg-zinc-800 border border-zinc-700 rounded-sm text-white text-xl flex items-center justify-center hover:bg-zinc-700"
              >+</button>
            </div>
            <p className="text-[10px] text-zinc-600">1 = juste toi, 2+ = toi + des invités</p>
          </div>
          <DialogFooter className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setShowJoinDialog(null)} className="flex-1 border-zinc-700 text-zinc-400">Annuler</Button>
            <Button onClick={() => handleJoin(showJoinDialog)} className="flex-1 bg-[#FF6B35] text-black hover:bg-[#E85A25]" data-testid="confirm-join">
              <Flame className="w-4 h-4 mr-1" /> C'est parti !
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Proof Dialog — soumission avec photo */}
      <Dialog open={!!showProofDialog} onOpenChange={(open) => { if (!open) { setShowProofDialog(null); setProofPhoto(''); setProofNote(''); } }}>
        <DialogContent className="bg-[#1B1B1B] border-[#2A2A2E] text-white max-w-sm">
          <DialogHeader><DialogTitle className="text-white">Soumettre ta preuve</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Photo de preuve</Label>
              {proofPhoto ? (
                <div className="relative">
                  <img src={proofPhoto} alt="preuve" className="w-full h-40 object-cover rounded-sm border border-[#2A2A2E]" />
                  <button onClick={() => setProofPhoto('')} className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              ) : (
                <MediaUpload
                  compact
                  onMediaAdded={(url) => setProofPhoto(url)}
                />
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Note (optionnel)</Label>
              <Input
                value={proofNote}
                onChange={e => setProofNote(e.target.value)}
                placeholder="Comment ca s'est passe..."
                className="bg-[#0F0F0F] border-[#2A2A2E] text-white"
                data-testid="proof-note"
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => { setShowProofDialog(null); setProofPhoto(''); setProofNote(''); }} className="flex-1 border-zinc-700 text-zinc-400">Annuler</Button>
            <Button onClick={() => handleComplete(showProofDialog)} className="flex-1 bg-[#FFB800] text-black hover:bg-[#FFD000]" data-testid="confirm-proof">
              <Check className="w-4 h-4 mr-1" /> Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#1B1B1B] border-[#2A2A2E] text-white max-w-md">
          <DialogHeader><DialogTitle className="text-white">Nouveau Challenge</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4" data-testid="create-challenge-form">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Titre *</Label>
              <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required placeholder="100 pompes par jour" className="bg-[#0F0F0F] border-[#2A2A2E] text-white" data-testid="challenge-title" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Description *</Label>
              <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} required placeholder="Fais 100 pompes chaque jour pendant 7 jours..." className="bg-[#0F0F0F] border-[#2A2A2E] text-white min-h-[60px]" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-400 text-[10px]">Date événement</Label>
                <Input type="date" value={form.event_date} onChange={e => setForm({...form, event_date: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white" data-testid="challenge-date" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-[10px]">Durée (jours)</Label>
                <Input type="number" value={form.duration_days} onChange={e => setForm({...form, duration_days: parseInt(e.target.value) || 7})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-[10px]">Points</Label>
                <Input type="number" value={form.reward_points} onChange={e => setForm({...form, reward_points: parseInt(e.target.value) || 10})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-[10px]">Badge (optionnel)</Label>
              <Input value={form.reward_badge} onChange={e => setForm({...form, reward_badge: e.target.value})} placeholder="Guerrier" className="bg-[#0F0F0F] border-[#2A2A2E] text-white" />
            </div>
            <DialogFooter className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="flex-1 border-zinc-700 text-zinc-400">Annuler</Button>
              <Button type="submit" className="flex-1 bg-[#FF6B35] text-black hover:bg-[#E85A25]" data-testid="submit-challenge">Créer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingChallenge} onOpenChange={(open) => { if (!open) { setEditingChallenge(null); setForm({ title: '', description: '', event_date: '', duration_days: 7, reward_points: 10, reward_badge: '' }); } }}>
        <DialogContent className="bg-[#1B1B1B] border-[#2A2A2E] text-white max-w-md">
          <DialogHeader><DialogTitle className="text-white">Modifier le defi</DialogTitle></DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4" data-testid="edit-challenge-form">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Titre *</Label>
              <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required className="bg-[#0F0F0F] border-[#2A2A2E] text-white" data-testid="edit-challenge-title" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Description *</Label>
              <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} required className="bg-[#0F0F0F] border-[#2A2A2E] text-white min-h-[60px]" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-400 text-[10px]">Date evenement</Label>
                <Input type="date" value={form.event_date} onChange={e => setForm({...form, event_date: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-[10px]">Duree (jours)</Label>
                <Input type="number" value={form.duration_days} onChange={e => setForm({...form, duration_days: parseInt(e.target.value) || 7})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-[10px]">Points</Label>
                <Input type="number" value={form.reward_points} onChange={e => setForm({...form, reward_points: parseInt(e.target.value) || 10})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" />
              </div>
            </div>
            <DialogFooter className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditingChallenge(null)} className="flex-1 border-zinc-700 text-zinc-400">Annuler</Button>
              <Button type="submit" className="flex-1 bg-[#FF6B35] text-black hover:bg-[#E85A25]" data-testid="submit-edit-challenge">Sauvegarder</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {!embedded && <BottomNav />}
    </div>
  );
}
